import { type ApolloCache } from '@apollo/client';
import { transform } from 'inflection';
import { compact, every } from 'lodash';

import { type Address, GetCurrentUserDocument, type OnboardingSteps } from '@/graphql/generated/graphql';

// This function now accepts anything that can have a length array, filelist, linkedlist, NodeList etc
function objectLength(object: ArrayLike<unknown> | null | undefined): number {
  return object?.length ?? 0;
}

/** @deprecated Use `objectLength` instead. */
const arrayLength = objectLength;

const titleizeCamelCase = (str: string) => transform(str, ['underscore', 'titleize']);

function isRichTextVisuallyEmpty(html: string | undefined | null): boolean {
  if (!html) return true;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
  return text.length === 0;
}

const getHtmlInputValue = (idSelector: string | null | undefined) => {
  if (!idSelector) return undefined;
  const el = document.getElementById(idSelector);

  if (el instanceof HTMLInputElement) {
    return el.value;
  } else {
    return undefined;
  }
};

const formatAddress = (address: Address | null | undefined, shorten?: boolean, showNoAddressText?: boolean) => {
  if (!address) return showNoAddressText ? 'No address associated' : '';

  const addressArray = [address.street, address.city, address.state, address.country, address.zip];

  return shorten ? `${address.street}, ${address.city}` : compact(addressArray).join(', ');
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number((bytes / Math.pow(k, i)).toFixed(1))} ${['B', 'KB', 'MB', 'GB'][i]}`;
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getSubProjectCodeInitials = (parentProjectCode: string | undefined) => {
  if (parentProjectCode) {
    if (parentProjectCode.includes('~')) {
      return `${parentProjectCode.split('~')[1]}~`;
    }
    return `${parentProjectCode}~`;
  }
  return '';
};

const updateOnboardingCache = (cache: ApolloCache, onboardingSteps: OnboardingSteps | null | undefined) => {
  if (onboardingSteps) {
    const currentUserData = cache.readQuery({
      query: GetCurrentUserDocument,
    });

    if (!currentUserData?.fetchMyData || !currentUserData?.fetchMyData.defaultCompany) return;

    cache.writeQuery({
      query: GetCurrentUserDocument,
      data: {
        fetchMyData: {
          ...currentUserData?.fetchMyData,
          welcomeChecklistCompleted: every(onboardingSteps, (step) => step === true),
          defaultCompany: {
            ...currentUserData?.fetchMyData?.defaultCompany,
            onboardingSteps,
          },
        },
      },
    });
  }
};

const combinedFormat = (dateTimePreference: { date?: string | undefined; time?: string | undefined }) => {
  return [dateTimePreference.date, dateTimePreference.time].filter(Boolean).join(' ') || undefined;
};

export {
  arrayLength,
  titleizeCamelCase,
  getHtmlInputValue,
  formatAddress,
  formatBytes,
  formatFileSize,
  getSubProjectCodeInitials,
  updateOnboardingCache,
  combinedFormat,
  objectLength,
  isRichTextVisuallyEmpty,
};
